import type { Cell, CellHistoryItem } from '@tabel/shared/schemas/cells';
import {
  type DayReceiptRow,
  type EngineCalcError,
  type EngineDayCalculated,
  type EngineDayLine,
  type TraceStepInfo,
  traceStepKey,
} from '@tabel/shared/schemas/day-trace';
import { AlertTriangle, CircleAlert, ListTree } from 'lucide-react';
import type { MouseEvent, ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { hours, ruDateTime } from '../lib/format';
import { rulesHref } from '../lib/nav';
import { TraceTable, stepCode } from './TraceTable';
import { useUnsavedGuard } from './UnsavedGuard';
import { Callout } from './ui';

/*
 * Карточка дня на движке (MVP C4, макет «чек и полоса проходов» 12.10.2026; 003 — правая панель): чек расчёта дня, ошибка расчёта и
 * «Подробно для разработчика» (трасса по шагам, версии движка и правил, строки дня) — свёрнуто. Строки чека и итог
 * собирает сервер (`engine.receipt`): тексты простым языком, значения правил из трассы, значение в формате R5.
 * Правку и код отсутствия знает ячейка табеля: строку «Правка» и итог поверх расчёта собирает окно из `cell` и
 * истории; источник документа дня (кто и в каком табеле поставил код) — тоже из истории. Полоса и таблица проходов —
 * `DayPasses.tsx`, карточка — `DayCard.tsx`.
 */

/** Коды ошибок расчёта (data-model А.5) словами; нового кода нет — код. Ещё — условие выборки дней (005). */
export const ERROR_TEXT: Readonly<Record<string, string>> = {
  INPUT_INCOMPLETE: 'не хватает входных данных — правил объекта, календаря или назначения на дату',
  STEP_THROWN: 'шаг завершился с ошибкой',
  OP_UNDECLARED: 'шаг сделал операцию, которую не объявил',
  INV_PASS_MUTATED: 'проходы изменились во время расчёта',
  INV_MIDNIGHT: 'отрезок времени переходит через полночь',
  INV_COVERAGE: 'отрезки смены перекрываются или не покрывают её',
  INV_DAY_24H: 'на дату больше 24 часов',
  INV_CHECKSUM: 'не сошлась контрольная сумма дня',
  INV_DOC_PLAN: 'часы явки и документа больше плана',
  INV_TABEL_LINE: 'строки нет в реестре строк табеля или она повторена',
};

/** Коды отсутствия табеля словами — для пилюли итога и карточки дня. */
export const ABSENCE_NAME: Readonly<Record<string, string>> = {
  О: 'отпуск',
  Б: 'больничный',
  ДО: 'без содержания',
};

/** Кто поправил: в строке правки и в итоге. */
const EDITOR: Readonly<Record<string, readonly [string, string]>> = {
  brigadier: ['бригадир', 'исправлено бригадиром'],
  hr: ['кадры', 'исправлено кадрами'],
};

/** Значение строки дня: в формате R5 с сервера; без R5 — точные часы с минутами. */
function lineValue(l: EngineDayLine): string {
  if (l.value !== null) return l.value;
  const min = Math.round((l.roundedMs ?? l.ms) / 60_000);
  return `${Math.floor(min / 60)}:${String(min % 60).padStart(2, '0')}`;
}

function lineText(l: EngineDayLine): string {
  return `${l.row} ${lineValue(l)}${l.overlay ? ' (наложение)' : ''}`;
}

const LABEL_TONE: Readonly<Record<NonNullable<DayReceiptRow['tone']> | 'none', string>> = {
  none: 'text-ink',
  warn: 'text-ink',
  bad: 'text-bad',
  muted: 'text-ink-3',
};
const NOTE_TONE: Readonly<Record<NonNullable<DayReceiptRow['tone']> | 'none', string>> = {
  none: 'text-ink-2',
  warn: 'text-warn',
  bad: 'text-bad',
  muted: 'text-ink-3',
};

/**
 * Ссылка из строки «Как посчитано» на правило (004, FR-014): «Правила расчёта» на дату дня, `#<правило>`. Переход —
 * через защиту несохранённого: правка в карточке дня не теряется молча. Клавиша-модификатор — как обычная ссылка.
 */
function RuleLink({ to, children }: { to: string; children: ReactNode }) {
  const { dirty, guard } = useUnsavedGuard();
  const navigate = useNavigate();
  const onClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (!dirty || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    guard(() => navigate(to));
  };
  return (
    <Link
      to={to}
      onClick={onClick}
      className="mt-0.5 block w-fit text-[12px] font-medium text-accent hover:underline max-tablet:flex max-tablet:min-h-10 max-tablet:items-center"
    >
      {children}
    </Link>
  );
}

/**
 * Строка чека: подпись (колонка фиксированной ширины), пояснение, значение справа. `source` — строки под пояснением
 * мелко: откуда значение (у документа — кто и в каком табеле поставил код). `rule` — строку дало правило объекта:
 * под пояснением ссылка на это правило, подпись — вопрос правила (004, FR-014, SC-015).
 */
function ReceiptLine({
  label,
  note,
  value,
  tone,
  source = [],
  rule,
  className = '',
}: {
  label: string;
  note: string;
  value: string;
  tone?: DayReceiptRow['tone'];
  source?: readonly string[];
  rule?: { to: string; question: string };
  className?: string;
}) {
  const t = tone ?? 'none';
  return (
    <div className={`flex items-baseline gap-3 border-t border-line px-4 py-2 first:border-t-0 ${className}`}>
      <span className={`w-[130px] flex-none max-sm:w-[96px] ${LABEL_TONE[t]}`}>{label}</span>
      <span className={`min-w-0 ${NOTE_TONE[t]}`}>
        {(tone === 'warn' || tone === 'bad') && (
          <CircleAlert
            size={16}
            strokeWidth={1.75}
            className="mr-1.5 inline-block align-[-3px]"
            aria-label="проверить"
          />
        )}
        {note}
        {source.map((line) => (
          <span key={line} className="block text-[12px] text-ink-3">
            {line}
          </span>
        ))}
        {rule && <RuleLink to={rule.to}>{rule.question}</RuleLink>}
      </span>
      <span className={`ml-auto pl-3 text-right whitespace-nowrap tabular-nums ${LABEL_TONE[t]}`}>
        {value}
      </span>
    </div>
  );
}

/** «дд.мм чч:мм» по смещению объекта. */
function shortStamp(iso: string, tzOffset: string): string {
  const full = ruDateTime(iso, tzOffset);
  return `${full.slice(0, 5)} ${full.slice(11)}`;
}

const isAbsence = (h: CellHistoryItem) => h.type === 'cell.absence_set' || h.type === 'cell.absence_clear';

/**
 * Где стоит код отсутствия дня: по каждому табелю — последняя запись о коде; она ставит этот код — источник. Код из
 * табеля другой бригады — тоже документ дня (расчёт читает все табели человека на дату).
 */
export function absenceSources(history: readonly CellHistoryItem[], code: string): CellHistoryItem[] {
  const last = new Map<number, CellHistoryItem>();
  for (const h of history) if (isAbsence(h)) last.set(h.timesheetId, h);
  return [...last.values()].filter(
    (h) =>
      h.type === 'cell.absence_set' && (h.after as { absenceCode?: unknown } | null)?.absenceCode === code,
  );
}

/** «поставлен в табеле «Бригада N» · кадры, Иванов Иван · 12.10 17:00»; этот табель — «в этом табеле». */
export function sourceText(h: CellHistoryItem, timesheetId: number, tzOffset: string): string {
  const where = h.timesheetId === timesheetId ? 'в этом табеле' : `в табеле «${h.timesheetName}»`;
  const who = [EDITOR[h.role ?? '']?.[0], h.author].filter(Boolean).join(', ');
  return [`поставлен ${where}`, who, shortStamp(h.ts, tzOffset)].filter(Boolean).join(' · ');
}

/**
 * Чек расчёта дня: строки сервера сверху вниз, правка (если есть) и итог «В табеле» крупно — пилюля кода с названием,
 * откуда значение и значение как в ячейке табеля (`finalValue`, формат R5: «9», не «9,0 ч»). Строка, которую дало
 * правило объекта (`ruleGroup`), ведёт на это правило в «Правилах расчёта» на дату дня (004, FR-014). Без ячейки
 * табеля (расчёт дня со страницы правил, только чтение) — итог по расчёту, без правки и кода отсутствия.
 */
export function EngineReceipt({
  engine,
  cell,
  history = [],
  timesheetId = 0,
  tzOffset,
  date,
}: {
  engine: EngineDayCalculated;
  cell?: Cell;
  history?: readonly CellHistoryItem[];
  /** Открытый табель: источник документа из него — «в этом табеле». */
  timesheetId?: number;
  tzOffset: string;
  /** Дата дня: ссылка на правило — правила на эту дату. */
  date: string;
}) {
  const { rows, total } = engine.receipt;
  const manual = cell?.manualHours != null;
  const editor = EDITOR[cell?.manualByRole ?? ''] ?? (['правка', 'исправлено'] as const);
  // Правка ячейки — этого табеля: в истории дня есть и записи других табелей человека.
  const lastEdit = [...history]
    .reverse()
    .find((h) => h.type === 'cell.edit' && h.timesheetId === timesheetId);
  const editNote = [
    cell?.manualComment ? `${editor[0]}: «${cell.manualComment}»` : editor[0],
    lastEdit ? shortStamp(lastEdit.ts, tzOffset) : null,
  ]
    .filter(Boolean)
    .join(' · ');
  const value = cell
    ? (cell.absenceCode ??
      cell.finalValue ??
      (manual ? hours(cell.manualHours ?? null) : null) ??
      total.value)
    : total.value;
  const codeText = (code: string | null, name: string | null) =>
    code === null ? null : name === null ? code : `${code} — ${name}`;
  let chip: string | null;
  let chipClass = 'bg-ok-soft text-ok';
  let note: string;
  if (cell?.absenceCode) {
    chip = codeText(cell.absenceCode, ABSENCE_NAME[cell.absenceCode] ?? null);
    chipClass = 'bg-subtle text-ink-2 ring-1 ring-inset ring-line';
    note = 'код отсутствия';
  } else if (manual) {
    const base = codeText(total.code, total.name);
    chip = `✎ ${base ?? 'правка'}`;
    chipClass = 'bg-edit text-warn';
    note = editor[1];
  } else {
    chip = codeText(total.code, total.name);
    // День по документу: часы — из документа, отметка «в обед» итог не меняет.
    const byPasses = total.value !== null && !rows.some((r) => r.kind === 'документ');
    note = cell?.lunch && byPasses ? 'по проходам СКУД, отметка «работал в обед»' : total.note;
  }
  const bad = engine.error !== null;
  return (
    <div className="overflow-hidden rounded-card border border-line">
      {rows.map(({ ruleGroup, ruleQuestion, ...r }) => (
        <ReceiptLine
          key={`${r.kind}-${r.label}`}
          {...r}
          source={
            r.kind === 'документ'
              ? absenceSources(history, r.value).map((h) => sourceText(h, timesheetId, tzOffset))
              : undefined
          }
          rule={
            ruleGroup && ruleQuestion
              ? { to: rulesHref({ date, group: ruleGroup }), question: ruleQuestion }
              : undefined
          }
        />
      ))}
      {manual && (
        <ReceiptLine
          label="Правка"
          note={editNote}
          value={`${cell?.machineValue ?? '—'} → ${value ?? '—'}`}
          className="bg-edit/50"
        />
      )}
      <div className="flex items-center gap-3 border-t border-line bg-subtle px-4 py-3 first:border-t-0">
        <span className="w-[130px] flex-none text-[15px] font-semibold text-ink max-sm:w-[96px]">
          В табеле
        </span>
        {chip !== null && (
          <span className={`rounded-full px-2.5 py-0.5 text-[12px] whitespace-nowrap ${chipClass}`}>
            {chip}
          </span>
        )}
        <span className={`min-w-0 ${bad ? 'text-bad' : 'text-ink-2'}`}>{note}</span>
        <span
          className={`ml-auto pl-3 text-[22px] font-semibold tabular-nums ${value === null ? 'text-ink-3' : 'text-ink'}`}
        >
          {value ?? '—'}
        </span>
      </div>
    </div>
  );
}

/** Результата дня ещё нет: пересчёт не дошёл. */
export function EngineNotCalculated() {
  return (
    <div className="rounded-card border border-line bg-subtle px-4 py-3 text-ink-2">
      Часы за этот день ещё не посчитаны: пересчёт — после загрузки проходов, в 00:05 и при запуске.
    </div>
  );
}

function stepText(e: EngineCalcError, steps: Readonly<Record<string, TraceStepInfo>>): string {
  if (e.step === null) return 'проверка ядра или входные данные';
  const key = traceStepKey(e.step, e.step.impl);
  const title = Object.hasOwn(steps, key) ? steps[key]?.title : undefined;
  return [`шаг ${stepCode(e.step)}`, title, `v${e.step.impl}`].filter(Boolean).join(' · ');
}

function ErrorCallout({
  label,
  error,
  steps,
  note,
}: {
  label: string;
  error: EngineCalcError;
  steps: Readonly<Record<string, TraceStepInfo>>;
  note: string;
}) {
  return (
    <Callout tone="bad" icon={AlertTriangle}>
      <p className="font-semibold">{`${label}: ${ERROR_TEXT[error.code] ?? error.code}`}</p>
      <p className="mt-0.5 text-[12px] text-ink-2">{`код ${error.code} · ${stepText(error, steps)} · ${note}`}</p>
    </Callout>
  );
}

/**
 * Ошибки расчёта дня и периода, затем «Подробно для разработчика» — свёрнуто: версии движка и правил, строки дня,
 * трасса дня и строки трассы периода за этот день.
 */
export function EngineDayTrace({ engine, tzOffset }: { engine: EngineDayCalculated; tzOffset: string }) {
  const e = engine;
  return (
    <div className="space-y-3">
      {e.error && (
        <ErrorCallout
          label="Ошибка расчёта"
          error={e.error}
          steps={e.steps}
          note="ячейка пустая, пока ошибку не исправят"
        />
      )}
      {e.periodError && (
        <ErrorCallout
          label="Ошибка расчёта периода"
          error={e.periodError}
          steps={e.steps}
          note="показа и итогов периода нет"
        />
      )}
      <details className="group rounded-control border border-line">
        {/* До 900 px (панель на весь экран) — цель нажатия не ниже 40 px (T103). */}
        <summary className="flex cursor-pointer items-start gap-1.5 px-3 py-2 text-ink select-none max-tablet:min-h-10 max-tablet:py-2.5">
          <ListTree size={16} strokeWidth={1.75} className="mt-0.5 shrink-0 text-ink-3" aria-hidden />
          {/* Одной строкой текста: в узкой панели пояснение переносится под заголовок, а не колонкой рядом. */}
          <span className="min-w-0">
            <span className="font-semibold">Подробно для разработчика</span>{' '}
            <span className="text-ink-2">— трасса расчёта по шагам, версия движка и правил</span>
          </span>
        </summary>
        <div className="space-y-3 border-t border-line px-3 py-3">
          <div className="space-y-0.5 text-[12px] text-ink-2">
            <p className="tabular-nums">{`движок ${e.engineVersion} · правила, версия ${e.rulesetVersionId ?? '—'}`}</p>
            <p className="tabular-nums">
              {`строки дня: ${e.lines.length > 0 ? e.lines.map(lineText).join(' · ') : 'нет'}`}
              {e.offTabel.length > 0 && ` · вне табеля: ${e.offTabel.map(lineText).join(', ')}`}
            </p>
            {e.provisional && (
              <p className="text-warn">
                предварительно{e.computedAt ? `, посчитано ${ruDateTime(e.computedAt, tzOffset)}` : ''}
              </p>
            )}
          </div>
          <div>
            <p className="mb-2 text-[12px] text-ink-3">
              Только этот день и только шаги, которые что-то изменили, в порядке расчёта. Одинаковые строки
              подряд свёрнуты.
            </p>
            <TraceTable rows={e.trace} steps={e.steps} />
          </div>
          {e.periodTrace.length > 0 && (
            <div>
              <p className="mb-2 font-semibold text-ink">Показ и итоги периода за этот день</p>
              <TraceTable rows={e.periodTrace} steps={e.steps} />
            </div>
          )}
        </div>
      </details>
    </div>
  );
}
